import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { BUSINESS, APPLIANCE_TYPES, BRANDS } from "@/data/site";
import { CallButton } from "@/components/Blocks";
import { trackFormSubmit } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-lg border border-slate-600 bg-slate-800/80 px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20";
const labelCls = "block text-sm font-semibold text-slate-200 mb-1.5";

const EMPTY = {
    company: "", contact_name: "", phone: "", email: "", property_address: "",
    unit_number: "", tenant_name: "", tenant_phone: "", appliance_type: "",
    appliance_brand: "", model_number: "", problem: "", preferred_date: "",
    access_instructions: "", po_number: "", website: "",
};

export default function PropertyManagerForm() {
    const [form, setForm] = useState(EMPTY);
    const [status, setStatus] = useState("idle");
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            await axios.post(`${API}/property-manager-requests`, form);
            trackFormSubmit("property_manager_submit");
            setStatus("done");
        } catch (err) {
            setStatus("idle");
            toast.error("Something went wrong sending your request. Please call (321) 475-2230.");
        }
    };

    if (status === "done") {
        return (
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-8 text-center" data-testid="pm-success-panel">
                <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
                <h2 className="mt-4 font-heading text-2xl font-bold text-white">Priority request received</h2>
                <p className="mt-3 text-slate-300 max-w-md mx-auto leading-relaxed">
                    Thanks, <strong>{form.contact_name.split(" ")[0]}</strong> — your priority service request has been received.
                    Appliance Mania will contact you to <strong>confirm availability, scheduling, and access</strong> for {form.property_address} —
                    submitting this form does not confirm an appointment.
                    A confirmation email is on its way to <strong>{form.email}</strong>.
                </p>
                <p className="mt-4 text-slate-300">Need to reach us sooner?</p>
                <CallButton location="pm-form-success" className="mt-3" testid="pm-success-call-btn" />
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6 sm:p-8" data-testid="pm-request-form">
            <div className="grid gap-5 sm:grid-cols-2">
                <div>
                    <label className={labelCls} htmlFor="pm-company">Company / management company *</label>
                    <input id="pm-company" required className={inputCls} value={form.company} onChange={set("company")} placeholder="Company name (or 'Independent landlord')" data-testid="pm-company-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="pm-contact">Contact name *</label>
                    <input id="pm-contact" required className={inputCls} value={form.contact_name} onChange={set("contact_name")} placeholder="Your name" data-testid="pm-contact-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="pm-phone">Phone *</label>
                    <input id="pm-phone" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(321) 555-0100" data-testid="pm-phone-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="pm-email">Email *</label>
                    <input id="pm-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="pm-email-input" />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="pm-address">Property address *</label>
                    <input id="pm-address" required className={inputCls} value={form.property_address} onChange={set("property_address")} placeholder="Street address, city, ZIP" data-testid="pm-address-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="pm-unit">Unit # <span className="font-normal text-slate-500">(if applicable)</span></label>
                    <input id="pm-unit" className={inputCls} value={form.unit_number} onChange={set("unit_number")} placeholder="Unit / apt #" data-testid="pm-unit-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="pm-date">Preferred service date</label>
                    <input id="pm-date" type="date" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} data-testid="pm-date-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="pm-tenant">Tenant name <span className="font-normal text-slate-500">(if applicable)</span></label>
                    <input id="pm-tenant" className={inputCls} value={form.tenant_name} onChange={set("tenant_name")} data-testid="pm-tenant-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="pm-tphone">Tenant phone <span className="font-normal text-slate-500">(if applicable)</span></label>
                    <input id="pm-tphone" type="tel" className={inputCls} value={form.tenant_phone} onChange={set("tenant_phone")} data-testid="pm-tphone-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="pm-appliance">Appliance type *</label>
                    <select id="pm-appliance" required className={inputCls} value={form.appliance_type} onChange={set("appliance_type")} data-testid="pm-appliance-select">
                        <option value="">Select appliance…</option>
                        {APPLIANCE_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelCls} htmlFor="pm-brand">Brand</label>
                    <select id="pm-brand" className={inputCls} value={form.appliance_brand} onChange={set("appliance_brand")} data-testid="pm-brand-select">
                        <option value="">Select brand…</option>
                        {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                        <option value="Other">Other / not sure</option>
                    </select>
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="pm-model">Model number <span className="font-normal text-slate-500">(if available)</span></label>
                    <input id="pm-model" className={inputCls} value={form.model_number} onChange={set("model_number")} data-testid="pm-model-input" />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="pm-problem">Problem description *</label>
                    <textarea id="pm-problem" required rows={3} className={inputCls} value={form.problem} onChange={set("problem")} placeholder="What's the appliance doing?" data-testid="pm-problem-textarea" />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="pm-access">Access instructions <span className="font-normal text-slate-500">(lockbox, tenant will be home, office key…)</span></label>
                    <textarea id="pm-access" rows={2} className={inputCls} value={form.access_instructions} onChange={set("access_instructions")} data-testid="pm-access-textarea" />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="pm-po">PO / reference number <span className="font-normal text-slate-500">(if applicable)</span></label>
                    <input id="pm-po" className={inputCls} value={form.po_number} onChange={set("po_number")} data-testid="pm-po-input" />
                </div>
            </div>

            <div className="sr-only" aria-hidden="true">
                <label htmlFor="pm-website">Website</label>
                <input id="pm-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
            </div>

            <button type="submit" disabled={status === "sending"}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-bold px-6 py-4 rounded-xl shadow-lg transition-all text-base"
                data-testid="pm-submit-btn">
                {status === "sending" ? (<><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>) : "Submit Priority Service Request"}
            </button>
            <p className="mt-3 text-xs text-slate-500 text-center leading-relaxed">
                This sends a service request — it does not reserve or guarantee an appointment time. We will contact you to confirm.
                Urgent unit down? Call <a href={BUSINESS.tel} className="text-amber-400 font-semibold">{BUSINESS.phone}</a>.
            </p>
        </form>
    );
}
